import { Link } from "@inertiajs/react"

import AdminLayout from "../../../shared/AdminLayout"

interface AdminStayRow {
  id: number
  name: string
  detailsText: string
  editHref: string
}

interface AdminStaysProps {
  stays: AdminStayRow[]
  emptyText: string
  newHref: string
  addLabel: string
}

const ROW_CLASSES = "flex items-center gap-3 border-b border-gray-100 py-3"
const LAST_ROW_CLASSES = "flex items-center gap-3 py-3"

// The admin stays list (`Stays::AdminStaysController#index`,
// `/admin/stays`). Implements `docs/design/stays.html`'s Block 3 verbatim:
// a flat list (no sections, no grouping) ordered by check-in, tap-to-edit
// rows (one interactive zone, a chevron, no other control -- the
// `contacts/AdminContacts` idiom this mirrors, minus its sections), and a
// dashed «+ Добавить жильё» at the bottom, present even with zero stays,
// when the localized empty state renders in its place above it.
//
// At this child's own HEAD every row's `editHref` and `newHref` still
// 404 -- `impl-admin-form` has not landed the actions they point at yet.
// Expected, not a defect: the farm merges the task whole.
export default function AdminStays({ stays, emptyText, newHref, addLabel }: AdminStaysProps) {
  return (
    <AdminLayout mainClassName="flex flex-col gap-4 p-4">
      {stays.length > 0 ? (
        <ol className="flex flex-col">
          {stays.map((stay, index) => (
            <li key={stay.id}>
              <Link
                href={stay.editHref}
                className={index === stays.length - 1 ? LAST_ROW_CLASSES : ROW_CLASSES}
              >
                <div className="flex grow flex-col gap-px">
                  <div className="text-[15px] font-semibold leading-[22px] text-gray-900">
                    {stay.name}
                  </div>
                  <div className="text-[13px] leading-[18px] text-gray-500">
                    {stay.detailsText}
                  </div>
                </div>
                <svg
                  aria-hidden="true"
                  className="text-gray-400"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M6 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </li>
          ))}
        </ol>
      ) : (
        <p className="px-6 py-16 text-center text-[15px] text-gray-500">{emptyText}</p>
      )}
      <Link
        href={newHref}
        className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-gray-300 text-sm font-medium text-accent"
      >
        {addLabel}
      </Link>
    </AdminLayout>
  )
}
